import { useRef, useCallback, useEffect } from 'react';

/**
 * useTimeoutRef - Manages a single timeout with automatic cleanup
 *
 * Returns a tuple with:
 * - set: Function to set a new timeout (clears any existing one first)
 * - clear: Function to clear the current timeout
 *
 * The timeout is automatically cleared on component unmount.
 *
 * @returns [set, clear] - Functions to manage the timeout
 */
export function useTimeoutRef(): [
  set: (callback: () => void, delay: number) => void,
  clear: () => void
] {
  const timeoutRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const set = useCallback((callback: () => void, delay: number) => {
    clear();
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      callback();
    }, delay);
  }, [clear]);

  // Cleanup on unmount
  useEffect(() => {
    return clear;
  }, [clear]);

  return [set, clear];
}

/**
 * useTimeoutRefs - Manages multiple timeouts with automatic cleanup
 *
 * Useful when you need to track multiple concurrent timeouts
 * (e.g., staged animations).
 *
 * Returns a tuple with:
 * - push: Function to add a new timeout
 * - clearAll: Function to clear all timeouts
 *
 * All timeouts are automatically cleared on component unmount.
 *
 * @returns [push, clearAll] - Functions to manage timeouts
 */
export function useTimeoutRefs(): [
  push: (callback: () => void, delay: number) => void,
  clearAll: () => void
] {
  const timeoutsRef = useRef<number[]>([]);

  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const push = useCallback((callback: () => void, delay: number) => {
    const id = window.setTimeout(callback, delay);
    timeoutsRef.current.push(id);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return clearAll;
  }, [clearAll]);

  return [push, clearAll];
}
